import React, { useCallback, useMemo, useState } from 'react';
import {
  SideBarItem,
  UpsellStudyModal,
  InformationBox,
  SideMenuPage,
} from 'components';
import { Session, SessionStatus, StudyType, useIsAdmin } from '@app/api';
import { useEditSessionSteps } from '../steps';
import { EditSessionNavItem } from './EditSessionNavItem';
import { useCurrentStepIndex } from '../hooks/useSessionSteps';
import { createUseStyles } from 'react-jss';
import { EditSessionLayoutSidePanelFooter } from './EditSessionLayoutSidePanelFooter';
import classNames from 'classnames';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  children: React.ReactNode;
  session: Session;
  footer?: React.ReactNode;
  contentClassName?: string;
  rightMenuOpened?: boolean;
  extraRightMenu?: React.ReactNode;
}

const useStyles = createUseStyles((theme: Theme) => ({
  fullpage: {
    height: '100vh',
  },
  content: {
    maxWidth: theme.maxContentWidth.largeReduced,
    width: '100%',
    margin: 'auto',
    padding: 0,
  },
  informationBox: {
    padding: theme.marginBase,
    marginTop: theme.marginBase * 3,
  },
  contentInfo: {
    whiteSpace: 'pre-line',
  },
  adminLink: {
    ...theme.fonts.label,
    marginTop: theme.marginBase * 4,
    color: theme.colors.purpleAccessible,
  },
  button: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
    padding: 0,
    paddingLeft: theme.marginBase / 2,
    margin: 0,
    width: 'fit-content',
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

const useGetRelativeMatchPosition = (session: Session) => {
  const { steps } = useEditSessionSteps(session);
  return useCallback(
    (currentIndex: number, path: string) => {
      const linkIndex = steps.map((step) => step.path).indexOf(path);
      if (linkIndex < 0) {
        return 0;
      }
      return linkIndex - currentIndex;
    },
    [steps],
  );
};

export const EditSessionLayout = ({
  session,
  children,
  footer,
  contentClassName,
  rightMenuOpened,
  extraRightMenu,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const currentStep = useCurrentStepIndex(session);
  const { steps, isLoading: isStepsLoading } = useEditSessionSteps(session);
  const isAdmin = useIsAdmin();
  const getRelativeMatchPosition = useGetRelativeMatchPosition(session);
  const [supportModalOpened, setOpenSupportModal] = useState(false);

  const menuItems = useMemo(
    () =>
      isStepsLoading
        ? []
        : steps.map((step) => (
            <EditSessionNavItem
              key={step.path}
              text={step.link}
              to={`../${step.path}`}
              relativeMatchPosition={getRelativeMatchPosition(
                currentStep,
                step.path,
              )}
              completed={
                session.validFormSteps &&
                session.validFormSteps.includes(step.name)
              }
            />
          )),
    [currentStep, session.validFormSteps, steps, isStepsLoading],
  );

  return (
    <div className={classes.fullpage}>
      <SideMenuPage
        closeTo={`/sessions/details/${session.id}/`}
        navTitle={
          session.status === SessionStatus.DRAFT
            ? t('editSession.nav.createTitle')
            : t('editSession.nav.editTitle')
        }
        hideNavOnTablet
        contentFooter={footer}
        menuItems={menuItems}
        rightMenuOpened={rightMenuOpened}
        extraRightMenu={extraRightMenu}
        footer={<EditSessionLayoutSidePanelFooter session={session} />}
        sideBarContent={
          <>
            {session.studyType === StudyType.MODERATED && (
              <InformationBox noIcon className={classes.informationBox}>
                <div className={classes.contentInfo}>
                  <Trans
                    i18nKey="editSession.upsell.description"
                    components={{
                      a: (
                        <a
                          onClick={() => {
                            setOpenSupportModal(true);
                          }}
                          className={classes.button}
                        />
                      ),
                    }}
                  />
                  <UpsellStudyModal
                    isOpen={supportModalOpened}
                    onRequestClose={() => setOpenSupportModal(false)}
                    sessionId={session.id}
                  />
                </div>
              </InformationBox>
            )}

            {isAdmin && (
              <SideBarItem
                text={t('editSession.nav.admin')}
                to="../admin"
                icon="ri-admin-line"
                className={classes.adminLink}
              />
            )}
          </>
        }
      >
        <div className={classNames(classes.content, contentClassName)}>
          {children}
        </div>
      </SideMenuPage>
    </div>
  );
};
